import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * Smart scroll restoration:
 *  - PUSH / REPLACE (a new navigation, e.g. clicking a link): scroll to top.
 *  - POP (back/forward via the browser): restore the previous scroll Y.
 *
 * Scroll positions are stashed in sessionStorage keyed by pathname so they
 * survive a full page reload too. Place once, inside <BrowserRouter>.
 */
export default function ScrollToTop() {
  const { pathname } = useLocation();
  const navType = useNavigationType(); // "POP" | "PUSH" | "REPLACE"
  const lastPath = useRef(pathname);

  // Persist the outgoing path's scroll position whenever the pathname changes.
  useEffect(() => {
    const prev = lastPath.current;
    if (prev && prev !== pathname) {
      try {
        sessionStorage.setItem(`scroll:${prev}`, String(window.scrollY));
      } catch { /* sessionStorage may be unavailable */ }
    }
    lastPath.current = pathname;
  }, [pathname]);

  useEffect(() => {
    if (navType === "POP") {
      let saved = 0;
      try {
        saved = parseInt(sessionStorage.getItem(`scroll:${pathname}`) || "0", 10) || 0;
      } catch { /* noop */ }
      // Delay one frame so the new route has rendered before we scroll.
      requestAnimationFrame(() => window.scrollTo({ top: saved, left: 0, behavior: "instant" }));
    } else {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }
  }, [pathname, navType]);

  return null;
}
